import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Calendar, Cloud, Building2, Network, Plus, Edit2, Trash2, AlertCircle, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import QrSection from '../components/settings/QrSection';
import NotificationsSection from '../components/settings/NotificationsSection';

/**
 * Tab "Praxis" — practice profile, public page+QR, smart-timing, calendars,
 * CSV import, auto-pull-forward, Google Reviews. PLUS (Hauptstandort only)
 * the multi-location management + group overview sections.
 */
export default function SettingsPraxisPage() {
  const { t } = useTranslation();
  const { user, practice, setPractice } = useAuth();
  const [form, setForm] = useState({});
  const [industries, setIndustries] = useState([]);
  const [calendar, setCalendar] = useState({ google: null, outlook: null, ical: null });
  const [icalUrl, setIcalUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/api/industries').then((r) => setIndustries(r.data.industries || [])); }, []);

  // Handle OAuth round-trip return: /einstellungen/praxis?calendar=google_connected|error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('calendar');
    if (!status) return;
    const reason = params.get('reason');
    if (status === 'google_connected' || status === 'outlook_connected') {
      const kind = status === 'google_connected' ? 'Google' : 'Outlook';
      toast.success(t('settings.calendars.connectedToast', { kind }));
      api.get('/api/calendar/status').then((r) => setCalendar(r.data)).catch(() => {});
    } else if (status === 'error') {
      const detail = reason ? ` (${reason})` : '';
      toast.error(t('settings.calendars.connectFailed') + detail);
    }
    // Clean the URL.
    window.history.replaceState({}, '', '/einstellungen/praxis');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!practice) return;
    setForm({
      name: practice.name || '', description: practice.description || '', address: practice.address || '',
      practice_type: practice.practice_type || 'wahlarzt',
      hourly_rate: practice.hourly_rate || 100,
      public_enabled: practice.public_enabled !== false,
      auto_waitlist_activation: practice.auto_waitlist_activation !== false,
      pull_forward_enabled: practice.pull_forward_enabled !== false,
      max_pull_forward_chain: practice.max_pull_forward_chain || 3,
      pull_forward_window_days: practice.pull_forward_window_days || 30,
      token_ttl_min: practice.token_ttl_min || 30,
      smart_timing_enabled: practice.smart_timing_enabled !== false,
      predictive_enabled: practice.predictive_enabled !== false,
      google_review_enabled: practice.google_review_enabled === true,
      google_review_url: practice.google_review_url || '',
      google_review_delay_hours: practice.google_review_delay_hours || 24,
    });
    api.get('/api/calendar/status').then((r) => setCalendar(r.data)).catch(() => {});
    setIcalUrl(practice.ical_feed_url || '');
  }, [practice]);

  function setF(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  async function save() {
    if (!practice) return;
    setSaving(true);
    try { const r = await api.patch(`/api/practices/${practice._id}`, form); setPractice(r.data.practice); toast.success(t('settings.saveSuccess')); }
    catch (_) { toast.error(t('errors.unknown')); }
    finally { setSaving(false); }
  }
  async function connectCal(kind) {
    try {
      // Try real OAuth2 first. If backend says CALENDAR_PROVIDER=mock,
      // fall back to legacy direct-connect for preview environments.
      try {
        const init = await api.post(`/api/auth/${kind}/init`, {});
        if (init.data?.authorize_url) {
          window.location.href = init.data.authorize_url;
          return;
        }
      } catch (initErr) {
        const code = initErr.response?.data?.error;
        if (code !== 'provider_mock') {
          // 'oauth_not_configured' or anything else from real-mode → show real msg.
          toast.error(initErr.response?.data?.message || t('errors.unknown'));
          return;
        }
        // provider_mock → fall through to legacy direct-connect below.
      }
      await api.post(`/api/calendar/${kind}/connect`);
      const r = await api.get('/api/calendar/status'); setCalendar(r.data);
      toast.success(`${kind === 'google' ? 'Google' : 'Outlook'} verbunden`);
    } catch (_) { toast.error(t('errors.unknown')); }
  }
  async function disconnectCal(kind) {
    await api.post(`/api/calendar/${kind}/disconnect`); const r = await api.get('/api/calendar/status'); setCalendar(r.data);
  }
  async function saveIcal() {
    if (!icalUrl) return;
    try { const r = await api.post('/api/calendar/ical', { url: icalUrl }); toast.success(`Sync OK · ${r.data.synced}`); const s = await api.get('/api/calendar/status'); setCalendar(s.data); }
    catch (_) { toast.error('Sync fehlgeschlagen'); }
  }

  if (!practice) return null;
  const isMain = practice.is_main_location === true;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Section title={t('settings.sections.industryPrice')}>
        <Field label={t('settings.industry.label')}>
          <select value={form.practice_type || 'wahlarzt'} onChange={(e) => setF('practice_type', e.target.value)} className="so-input" data-testid="set-practice-type">
            {industries.map((i) => <option key={i.id} value={i.id}>{i.label_de}</option>)}
          </select>
        </Field>
        <Field label={t('settings.industry.hourlyRate')}>
          <input type="number" min="0" value={form.hourly_rate || ''} onChange={(e) => setF('hourly_rate', Number(e.target.value))} className="so-input" data-testid="set-hourly-rate"/>
        </Field>
      </Section>

      <Section title={t('settings.sections.practiceProfile')}>
        <Field label={t('settings.profile.name')}><input className="so-input" value={form.name || ''} onChange={(e) => setF('name', e.target.value)} data-testid="set-name"/></Field>
        <Field label={t('settings.profile.description')}><textarea rows={2} className="so-input" value={form.description || ''} onChange={(e) => setF('description', e.target.value)} data-testid="set-description"/></Field>
        <Field label={t('settings.profile.address')}><input className="so-input" value={form.address || ''} onChange={(e) => setF('address', e.target.value)} data-testid="set-address"/></Field>
      </Section>

      <QrSection practice={practice} form={form} setF={setF} t={t}/>

      <Section title={t('settings.sections.autoActivation')}>
        <label className="flex items-start gap-3 mb-3"><input type="checkbox" className="mt-0.5" checked={!!form.auto_waitlist_activation} onChange={(e) => setF('auto_waitlist_activation', e.target.checked)} data-testid="set-auto-activation"/><span className="text-sm">{t('settings.activation.autoSlot')}</span></label>
        <label className="flex items-start gap-3 mb-3"><input type="checkbox" className="mt-0.5" checked={!!form.pull_forward_enabled} onChange={(e) => setF('pull_forward_enabled', e.target.checked)} data-testid="set-pull-forward"/><span className="text-sm">{t('settings.activation.pullForward')}</span></label>
        <Field label={t('settings.activation.windowDays', { days: form.pull_forward_window_days })}>
          <select value={form.pull_forward_window_days || 30} onChange={(e) => setF('pull_forward_window_days', Number(e.target.value))} className="so-input" data-testid="set-pf-window">
            <option value={7}>7 Tage</option><option value={14}>14 Tage</option><option value={30}>30 Tage</option>
          </select>
        </Field>
      </Section>

      <NotificationsSection form={form} setF={setF} t={t}/>

      <Section title={t('settings.sections.calendars')}>
        <Connector icon={Cloud} label="Google Calendar" connected={!!calendar.google} email={calendar.google?.account_email} onConnect={() => connectCal('google')} onDisconnect={() => disconnectCal('google')} testid="cal-google" t={t}/>
        <div className="mt-3">
          <Connector icon={Cloud} label="Microsoft Outlook" connected={!!calendar.outlook} email={calendar.outlook?.account_email} onConnect={() => connectCal('outlook')} onDisconnect={() => disconnectCal('outlook')} testid="cal-outlook" t={t}/>
        </div>
        <div className="mt-3 rounded-md border border-border p-3">
          <div className="flex items-center gap-2 mb-2"><Calendar size={14}/><span className="text-sm font-medium">iCal Feed</span></div>
          <input value={icalUrl} onChange={(e) => setIcalUrl(e.target.value)} placeholder="https://kalender.example.com/feed.ics" className="so-input font-mono text-xs" data-testid="cal-ical-url"/>
          <button onClick={saveIcal} className="so-btn so-btn-secondary text-xs mt-2" data-testid="cal-ical-save">{t('settings.calendars.icalSave')}</button>
        </div>
      </Section>

      {/* Hauptstandort-only sections */}
      {isMain && user?.role === 'admin' && (
        <>
          <LocationManagementSection className="lg:col-span-2" t={t}/>
          <GroupOverviewSection className="lg:col-span-2" t={t}/>
        </>
      )}

      <div className="lg:col-span-2 flex items-center justify-end">
        <button onClick={save} disabled={saving} className="so-btn so-btn-primary text-base px-6" data-testid="settings-save">{saving ? '…' : t('settings.saveAll')}</button>
      </div>
    </div>
  );
}

// ─────────────────  LocationManagementSection  ─────────────────────────
function LocationManagementSection({ className, t }) {
  const [list, setList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [planLimit, setPlanLimit] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', description: '', slug: '', admin_email: '', hourly_rate: 100 });
  const [submitting, setSubmitting] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState(null);

  const load = React.useCallback(async () => {
    try { const r = await api.get('/api/locations/group-summary'); setList(r.data.locations || []); }
    catch (_) { /* swallow */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function createBranch(e) {
    e.preventDefault();
    if (!form.name || !form.address || !form.admin_email) {
      toast.error(t('settings.locationMgmt.errors.required'));
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/api/locations', { ...form, hourly_rate: Number(form.hourly_rate) || 100 });
      toast.success(t('settings.locationMgmt.created'));
      setLastInviteUrl(r.data.invite_accept_url || null);
      setShowCreate(false);
      setForm({ name: '', address: '', description: '', slug: '', admin_email: '', hourly_rate: 100 });
      load();
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === 'multi_location_requires_pro') {
        setPlanLimit(err.response.data);
        toast.error(t('settings.locationMgmt.errors.starterUpgrade'));
      } else {
        toast.error(err.response?.data?.message || t('settings.locationMgmt.errors.generic'));
      }
    } finally { setSubmitting(false); }
  }
  async function deactivateBranch(id, name) {
    if (!window.confirm(t('settings.locationMgmt.deactivateConfirm', { name }))) return;
    try { await api.delete(`/api/locations/${id}`); toast.success(t('settings.locationMgmt.deactivated')); load(); }
    catch (e) { toast.error(e.response?.data?.message || t('settings.locationMgmt.errors.generic')); }
  }

  return (
    <section className={`so-card p-5 ${className || ''}`} data-testid="location-mgmt-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2"><Building2 size={14} className="text-accent"/>{t('settings.locationMgmt.title')}</h2>
          <p className="text-xs text-text-secondary mt-1">{t('settings.locationMgmt.subtitle')}</p>
        </div>
        <button onClick={() => setShowCreate((v) => !v)} className="so-btn so-btn-primary text-xs" data-testid="location-add-btn">
          <Plus size={14}/>{showCreate ? t('common.cancel') : t('settings.locationMgmt.addBtn')}
        </button>
      </div>

      {planLimit && (
        <div className="rounded-md border border-warning/30 bg-warning/5 p-3 mb-4 flex items-start gap-2" data-testid="location-plan-limit">
          <AlertCircle size={14} className="text-warning mt-0.5 shrink-0"/>
          <div>
            <p className="text-sm font-semibold text-warning">{planLimit.message}</p>
            <a href="/einstellungen/abo" className="text-xs text-accent hover:underline">{t('settings.locationMgmt.upgradeCta')} →</a>
          </div>
        </div>
      )}

      {showCreate && (
        <form onSubmit={createBranch} className="rounded-md border border-border bg-background/40 p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="location-create-form">
          <label className="text-sm"><span className="block text-xs font-medium text-text-secondary mb-1">{t('settings.locationMgmt.form.name')} *</span>
            <input className="so-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="loc-name"/></label>
          <label className="text-sm"><span className="block text-xs font-medium text-text-secondary mb-1">{t('settings.locationMgmt.form.adminEmail')} *</span>
            <input type="email" className="so-input" required value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="loc-admin-email"/></label>
          <label className="text-sm md:col-span-2"><span className="block text-xs font-medium text-text-secondary mb-1">{t('settings.locationMgmt.form.address')} *</span>
            <input className="so-input" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="loc-address"/></label>
          <label className="text-sm md:col-span-2"><span className="block text-xs font-medium text-text-secondary mb-1">{t('settings.locationMgmt.form.description')}</span>
            <textarea rows={2} className="so-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="loc-description"/></label>
          <label className="text-sm"><span className="block text-xs font-medium text-text-secondary mb-1">{t('settings.locationMgmt.form.slug')}</span>
            <input className="so-input font-mono text-xs" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={t('settings.locationMgmt.form.slugAuto')} data-testid="loc-slug"/></label>
          <label className="text-sm"><span className="block text-xs font-medium text-text-secondary mb-1">{t('settings.locationMgmt.form.hourlyRate')}</span>
            <input type="number" min={0} className="so-input" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="loc-hourly"/></label>
          <div className="md:col-span-2 flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowCreate(false)} className="so-btn so-btn-secondary text-xs">{t('common.cancel')}</button>
            <button type="submit" disabled={submitting} className="so-btn so-btn-primary text-xs" data-testid="loc-create-submit">{submitting ? '…' : t('settings.locationMgmt.form.submit')}</button>
          </div>
        </form>
      )}

      {lastInviteUrl && (
        <div className="rounded-md border border-accent/30 bg-accent/5 p-3 mb-4 text-xs" data-testid="location-invite-banner">
          <p className="font-semibold text-accent mb-1 flex items-center gap-2"><Mail size={12}/>{t('settings.locationMgmt.inviteSent')}</p>
          <input readOnly value={lastInviteUrl} className="so-input font-mono text-[11px]" data-testid="location-invite-url"/>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="location-table">
          <thead className="text-left text-[11px] uppercase text-text-secondary border-b border-border">
            <tr>
              <th className="py-2 pr-2">{t('settings.locationMgmt.table.name')}</th>
              <th className="py-2 pr-2">{t('settings.locationMgmt.table.address')}</th>
              <th className="py-2 pr-2">{t('settings.locationMgmt.table.status')}</th>
              <th className="py-2 pr-2 text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((l) => (
              <tr key={l._id} className="border-b border-border last:border-b-0" data-testid={`location-row-${l.slug}`}>
                <td className="py-2 pr-2"><strong>{l.name}</strong>{l.is_main_location && <span className="ml-2 text-[10px] uppercase tracking-wider text-accent">★ {t('settings.locationMgmt.main')}</span>}</td>
                <td className="py-2 pr-2 text-xs text-text-secondary">{l.address}</td>
                <td className="py-2 pr-2">
                  {l.deactivated ? <span className="so-pill so-pill-neutral">{t('settings.locationMgmt.statusDeactivated')}</span>
                    : <span className="so-pill so-pill-success">{t('settings.locationMgmt.statusActive')}</span>}
                </td>
                <td className="py-2 pr-2 text-right">
                  {!l.is_main_location && !l.deactivated && (
                    <button onClick={() => deactivateBranch(l._id, l.name)} className="text-danger hover:bg-danger/10 rounded p-1" title={t('settings.locationMgmt.deactivate')} data-testid={`location-deactivate-${l.slug}`}>
                      <Trash2 size={14}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─────────────────  GroupOverviewSection  ──────────────────────────────
function GroupOverviewSection({ className, t }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/api/locations/group-summary').then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  function pickPractice(id) {
    api.defaults.headers['X-Practice-Id'] = id;
    localStorage.setItem('studioops_active_practice', id);
    window.location.reload();
  }
  return (
    <section className={`so-card p-5 ${className || ''}`} data-testid="group-overview-section">
      <h2 className="text-sm font-semibold flex items-center gap-2 mb-3"><Network size={14} className="text-accent"/>{t('settings.groupOverview.title')}</h2>
      <p className="text-xs text-text-secondary mb-4">{t('settings.groupOverview.subtitle')}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.locations.map((l) => (
          <button
            key={l._id}
            onClick={() => pickPractice(l._id)}
            className="rounded-md border border-border bg-white p-4 text-left hover:border-accent transition"
            data-testid={`group-loc-${l.slug}`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-sm">{l.name}</p>
              {l.is_main_location && <span className="text-[10px] uppercase tracking-wider text-accent shrink-0">★ {t('settings.locationMgmt.main')}</span>}
            </div>
            <p className="text-[11px] text-text-secondary mt-1 truncate">{l.address}</p>
            <div className="mt-3 space-y-1.5 text-[11px] text-text-secondary">
              <UsageRow label={t('settings.groupOverview.staffCol')} used={l.usage?.staff || 0} total={data.group.seats_limit} accent/>
              <UsageRow label={t('settings.groupOverview.msgCol')} used={l.usage?.messages_this_month || 0} total={data.group.messages_limit}/>
              <p>{t('settings.groupOverview.waitlistCount', { count: l.usage?.waitlist || 0 })}</p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
function UsageRow({ label, used, total, accent }) {
  const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between"><span>{label}</span><span className="font-mono">{used}{total ? ` / ${total}` : ''}</span></div>
      <div className="h-1 bg-border rounded-full overflow-hidden mt-0.5">
        <div className={`h-full ${pct >= 90 ? 'bg-danger' : pct >= 70 ? 'bg-warning' : accent ? 'bg-accent' : 'bg-success'}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}
function Section({ title, children, className = '' }) {
  return <div className={`so-card p-5 ${className}`}><h2 className="text-sm font-semibold mb-4">{title}</h2>{children}</div>;
}
function Field({ label, children }) {
  return <div className="mb-3"><label className="so-label">{label}</label>{children}</div>;
}
function Connector({ icon: Icon, label, connected, email, onConnect, onDisconnect, testid, t }) {
  return (
    <div className="rounded-md border border-border p-3 flex items-center justify-between" data-testid={testid}>
      <div className="flex items-center gap-3"><Icon size={16} className="text-accent"/>
        <div><p className="text-sm font-medium">{label}</p>{connected && email && <p className="text-xs text-text-secondary font-mono">{email}</p>}</div>
      </div>
      {connected
        ? <div className="flex items-center gap-2"><span className="so-pill so-pill-success">{t('settings.calendars.connected')}</span><button onClick={onDisconnect} className="text-xs text-danger hover:underline">{t('settings.calendars.disconnect')}</button></div>
        : <button onClick={onConnect} className="so-btn so-btn-secondary text-xs">{t('settings.calendars.connect')}</button>}
    </div>
  );
}
